export type GeneratedDocument = {
    id: string;
    fileName: string;
    s3Url: string;
    createdAt: string;
  };
  
  export type UploadProgress = {
    fileName: string;
    progress: number;
    error?: string;
  };
  
  export async function fetchWordDocuments(tenantId: string, page = 0, size = 10, search = "", sortBy = "createdAt", sortOrder = "desc") {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents?page=${page}&size=${size}&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error("Failed to fetch documents");
    }
    return response.json();
  }


  /** Usuwa dokument na podstawie ID */
  export async function deleteWordDocument(tenantId: string, documentId: string) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/${documentId}`, {
      method: "DELETE",
      credentials: 'include'
    });

    if (!response.ok) throw new Error("Błąd usuwania dokumentu");
  }

  /** Pobiera plik na podstawie ID */
  export async function downloadGeneratedDocument(tenantId: string, documentId: string, fileName: string): Promise<void> {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/download/${documentId}`, { credentials: 'include' });

    if (!response.ok) throw new Error("Błąd pobierania dokumentu");

    // Pobierz nazwę pliku z nagłówka Content-Disposition
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
      const match = disposition.match(/filename=\"?([^\";]+)\"?/);
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1]);
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // --- FOLDERY Wygenerowanych dokumentów ---
  export interface GeneratedFolder {
    id: string;
    name: string;
    createdAt?: string;
    parentId?: string | null;
  }

  export async function fetchGeneratedFolders(tenantId: string, parentId?: string) {
    const url = new URL(`/api/tenants/${tenantId}/generated-folders`, window.location.origin);
    if (parentId !== undefined) {
      url.searchParams.set('parentId', parentId);
    }
    const response = await fetch(url.toString(), { credentials: 'include' });
    if (!response.ok) throw new Error('Błąd ładowania folderów wygenerowanych dokumentów');
    return response.json();
  }

  export async function addGeneratedFolder(tenantId: string, name: string, parentId?: string | null) {
    const body: any = { name, parentId: parentId ?? null };
    const response = await fetch(`/api/tenants/${tenantId}/generated-folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include'
    });
    if (response.status === 409) {
      throw new Error('Folder o tej nazwie już istnieje w tej lokalizacji.');
    }
    if (!response.ok) throw new Error('Błąd dodawania folderu wygenerowanych dokumentów');
    return response.json();
  }

  export async function deleteGeneratedFolder(tenantId: string, folderId: string) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-folders/${folderId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Błąd usuwania folderu wygenerowanych dokumentów');
    return;
  }

  export async function fetchGeneratedFolderById(tenantId: string, folderId: string | null): Promise<GeneratedFolderTreeDto> {
    // If folderId is null, fetch root
    const url = folderId ? `/api/tenants/${tenantId}/generated-documents/${folderId}` : `/api/tenants/${tenantId}/generated-documents/tree`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Błąd ładowania folderu');
    return response.json();
  }

  export async function updateGeneratedFolderName(tenantId: string, folderId: string, newName: string) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-folders/${folderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Błąd edycji nazwy folderu wygenerowanych dokumentów');
    return response.json();
  }

  export async function updateGeneratedDocumentFolder(tenantId: string, documentId: string, folderId: string | null) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/${documentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderId }),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Błąd przenoszenia dokumentu do folderu');
    return response.json();
  }

  // DTO for tree node with files
  export interface FolderTreeWithGeneratedFilesDto {
    id: string;
    name: string;
    tenantId: string;
    createdAt: string;
    parentId: string | null;
    children: FolderTreeWithGeneratedFilesDto[];
    documents: GeneratedFileDto[];
  }

  export interface GeneratedFileDto {
    id: string;
    title: string;
    createdAt: string;
    folderId: string | null;
  }

  // Fetch tree of folders and files
  export async function fetchGeneratedDocumentsTree(tenantId: string): Promise<FolderTreeWithGeneratedFilesDto[]> {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/tree`, { credentials: 'include' });
    if (!response.ok) throw new Error('Błąd ładowania drzewa dokumentów');
    return response.json();
  }

  // Search generated documents
  export async function searchGeneratedDocuments(tenantId: string, query: string): Promise<GeneratedFileDto[]> {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
    if (!response.ok) throw new Error('Błąd wyszukiwania dokumentów');
    return response.json();
  }

  // DTO for folder tree (for /generated-documents/{folderId})
  export interface GeneratedFolderTreeDto {
    id: string | null;
    name: string | null;
    tenantId: string;
    createdAt: string | null;
    parentId: string | null;
    children: GeneratedFolderTreeDto[];
    documents: GeneratedFileDto[];
  }

  /** Zmienia nazwę dokumentu na podstawie ID */
  export async function updateGeneratedDocumentName(tenantId: string, documentId: string, folderId: string | null, newName: string) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/${documentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newName, folderId: folderId }),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Błąd edycji nazwy dokumentu');
    return response.json();
  }

  /** Usuwa dokument na podstawie ID */
  export async function deleteGeneratedDocument(tenantId: string, documentId: string) {
    const response = await fetch(`/api/tenants/${tenantId}/generated-documents/${documentId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Błąd usuwania dokumentu');
  }